import { cx } from "@jugl-web/utils";
import { FC, ReactNode } from "react";
import { Button } from "../../primitives";
import {
  SwipeableArea,
  SwipeableHandle,
} from "../MobileDrawer/SwipeableHeader";
import { ReactComponent as CheckIcon } from "./assets/check.svg";
import { ReactComponent as ClearIcon } from "./assets/clear.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";

export interface CXDrawerHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  showClearButton?: boolean;
  className?: string;
  /** Custom label for the submit button. Defaults to a check icon. */
  submitLabel?: ReactNode;
  isSubmitDisabled?: boolean;
  onSubmit?: () => void;
  onClear?: () => void;
  onClose: () => void;
}

export const CXDrawerHeader: FC<CXDrawerHeaderProps> = ({
  title,
  subtitle,
  showClearButton = false,
  submitLabel,
  isSubmitDisabled = false,
  onSubmit,
  className,
  onClear,
  onClose,
}) => {
  const hasActionsSection = !!onSubmit;

  return (
    <header
      className={cx(
        "relative flex items-center justify-between border-b border-solid border-dark-100 px-4 pt-[30px] pb-2.5",
        className
      )}
    >
      <SwipeableArea onSwipeDown={onClose}>
        <SwipeableHandle />
      </SwipeableArea>
      <div className="flex w-full items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <OutlineHeaderButton onClick={onClose}>
            <CloseIcon />
          </OutlineHeaderButton>
          <div className="flex flex-1 flex-col gap-0.5 overflow-hidden">
            <span className="truncate text-sm leading-[140%] font-medium tracking-[0.14px] text-dark">
              {title}
            </span>
            {subtitle && (
              <span className="truncate text-xs leading-[140%] font-normal text-dark-600">
                {subtitle}
              </span>
            )}
          </div>
        </div>
        {hasActionsSection && (
          <div className="flex shrink-0 items-center gap-3">
            {showClearButton && onClear && (
              <OutlineHeaderButton onClick={onClear}>
                <ClearIcon />
              </OutlineHeaderButton>
            )}
            <Button
              variant="primary"
              size="sm"
              iconStart={!submitLabel ? <CheckIcon /> : undefined}
              isDisabled={isSubmitDisabled}
              className={cx("jugl__mobile-pressable", !submitLabel && "size-8")}
              onClick={onSubmit}
            >
              {submitLabel}
            </Button>
          </div>
        )}
      </div>
    </header>
  );
};

const OutlineHeaderButton: FC<{
  children: ReactNode;
  className?: string;
  onClick: () => void;
}> = ({ children, className, onClick }) => {
  return (
    <button
      type="button"
      className={cx(
        "jugl__mobile-pressable flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-solid border-grey-300 bg-white",
        className
      )}
      onClick={onClick}
    >
      {children}
    </button>
  );
};
